import { useEffect, useRef, useState, type ReactNode } from "react";
import { Button, IconButton } from "./Button";
import { Notice } from "./Surfaces";

interface ModalProps {
  title: string;
  onClose: () => void;
  wide?: boolean;
  /** Quando verdadeiro, fechar pelo X, pelo fundo ou por Esc pede confirmação antes de descartar. */
  dirty?: boolean;
  children: ReactNode;
}

export function Modal({ title, onClose, wide, dirty, children }: ModalProps) {
  const [confirming, setConfirming] = useState(false);
  const body = useRef<HTMLDivElement>(null);
  const previousFocus = useRef<Element | null>(null);
  // O ouvinte de teclado é registrado uma vez; a referência mantém a versão atual da regra de fechar.
  const requestClose = useRef(() => {});
  useEffect(() => {
    requestClose.current = () => (dirty ? setConfirming(true) : onClose());
  });

  useEffect(() => {
    previousFocus.current = document.activeElement;
    const first = body.current?.querySelector<HTMLElement>("input:not([type='hidden']), select, textarea") ?? body.current?.querySelector<HTMLElement>("button, a[href]");
    first?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") requestClose.current();
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      if (previousFocus.current instanceof HTMLElement) previousFocus.current.focus();
    };
  }, []);

  return (
    <div className="ds-modal-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget) requestClose.current(); }}>
      <section className={`ds-modal${wide ? " ds-modal--wide" : ""}`} role="dialog" aria-modal="true" aria-label={title}>
        <header className="ds-modal__header">
          <h2 className="ds-h2">{title}</h2>
          <IconButton label="Fechar" onClick={() => requestClose.current()}>×</IconButton>
        </header>
        <div className="ds-modal__body" ref={body}>
          {confirming ? (
            <div style={{ marginBottom: 14 }}>
              <Notice tone="danger" action={<span className="ds-row"><Button size="small" onClick={() => setConfirming(false)}>Continuar editando</Button><Button size="small" tone="danger" onClick={onClose}>Descartar</Button></span>}>
                Descartar o que foi digitado?
              </Notice>
            </div>
          ) : null}
          {children}
        </div>
      </section>
    </div>
  );
}

export const ModalFooter = ({ start, children }: { start?: ReactNode; children: ReactNode }) => (
  <div className="ds-modal__footer">
    <span className="ds-row">{start}</span>
    <span className="ds-row">{children}</span>
  </div>
);
